import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, DollarSign, School, Users, AlertTriangle, CheckCircle2, 
  Clock, ArrowRight, FileText, Truck, Calendar, Bell, ChevronRight
} from 'lucide-react';

export default function MOEDashboard() {
  const [stats, setStats] = useState({
    titlesForReview: 34,
    quotesAwaiting: 38,
    schoolsNotSubmitted: 39,
    mksAcknowledged: 42
  });

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Programme Status Banner */}
      <Card className="bg-gradient-to-r from-[#1A3C6E] to-[#2E5F8A] text-white border-0">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="font-heading text-lg font-semibold mb-2">CDS Pilot — ACTIVE</h2>
              <p className="text-white/80 text-sm">
                Pilot Cycle 1: Aug–Oct 2026 ✅ Complete | Pilot Cycle 2: In Progress (Nov 2026) | CDS Cycle 1 2028: Selection Phase
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-400" />
                <span className="text-sm">Pilot C1</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-sm">Pilot C2</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-400" />
                <span className="text-sm">CDS 2028 C1</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-slate-400" />
                <span className="text-sm">CDS 2028 C2</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="kpi-card group" data-testid="kpi-titles-review">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-amber-600" />
              </div>
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                Pending
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 mb-1">{stats.titlesForReview}</div>
            <p className="text-sm text-slate-500 mb-3">Curated Titles Pending Approval</p>
            <p className="text-xs text-slate-400 mb-4">Submitted by Civica — CDS Cycle 1 2028</p>
            <Button asChild size="sm" className="w-full bg-[#1A3C6E] hover:bg-[#142E54]">
              <Link to="/moe/curation-approval" data-testid="review-titles-btn">
                Review Now <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="kpi-card group" data-testid="kpi-quotes-awaiting">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                Review
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 mb-1">{stats.quotesAwaiting}</div>
            <p className="text-sm text-slate-500 mb-3">Supplier Quotes Awaiting</p>
            <p className="text-xs text-slate-400 mb-4">Civica recommendation submitted</p>
            <Button asChild size="sm" variant="outline" className="w-full">
              <Link to="/moe/quote-approval" data-testid="approve-quotes-btn">
                Approve Quotes <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="kpi-card group" data-testid="kpi-schools-pending">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <School className="w-5 h-5 text-orange-600" />
              </div>
              <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700">
                <AlertTriangle className="w-3 h-3 mr-1" />
                5 days
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 mb-1">{stats.schoolsNotSubmitted} <span className="text-lg font-normal text-slate-400">of 287</span></div>
            <p className="text-sm text-slate-500 mb-3">Schools Yet to Select Titles</p>
            <p className="text-xs text-slate-400 mb-4">Deadline: 15 Sep 2026</p>
            <Button asChild size="sm" variant="outline" className="w-full">
              <Link to="/moe/school-monitor" data-testid="view-schools-btn">
                View Status <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="kpi-card group" data-testid="kpi-mks">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                On Track
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 mb-1">{stats.mksAcknowledged}<span className="text-lg font-normal text-slate-400">/58</span></div>
            <p className="text-sm text-slate-500 mb-3">MK Programme 2026</p>
            <p className="text-xs text-slate-400 mb-4">Annual invoice pending</p>
            <Button asChild size="sm" variant="outline" className="w-full">
              <Link to="/moe/mk-programme" data-testid="view-mk-btn">
                View <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Action Items */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#1A3C6E]" />
              My Action Items
            </CardTitle>
            <CardDescription>Tasks requiring your attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
                <div className="w-2 h-2 rounded-full bg-red-500 mt-2" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="destructive" className="text-[10px]">OVERDUE</Badge>
                  </div>
                  <p className="text-sm text-slate-900">Approve 3 replacement curated titles submitted by Civica</p>
                  <p className="text-xs text-slate-500 mt-1">Was due yesterday</p>
                </div>
                <Button size="sm" variant="destructive" data-testid="action-overdue">
                  Review
                </Button>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-2" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-amber-100 text-amber-700 text-[10px]">TODAY</Badge>
                  </div>
                  <p className="text-sm text-slate-900">Review 34 pending curated titles — Cycle 1 2028</p>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/moe/curation-approval">Review</Link>
                </Button>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-2" />
                <div className="flex-1">
                  <p className="text-sm text-slate-900">Verify Civica evidence for open call quotes — 38 titles</p>
                  <p className="text-xs text-slate-500 mt-1">Due in 3 days</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                <div className="flex-1">
                  <p className="text-sm text-slate-900">Monitor school selections — 39 schools outstanding</p>
                  <p className="text-xs text-slate-500 mt-1">Due in 5 days</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                <div className="flex-1">
                  <p className="text-sm text-slate-900">Verify Pilot Cycle 2 November delivery — MK acknowledgements</p>
                  <p className="text-xs text-slate-500 mt-1">Oct 2026</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Programme Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#1A3C6E]" />
              Programme Timeline
            </CardTitle>
            <CardDescription>Key milestones</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Mar 2026</p>
                  <p className="text-sm text-slate-500">Pilot commences</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Aug–Oct 2026</p>
                  <p className="text-sm text-slate-500">Pilot Cycle 1 deliveries (5 schools × 20 books × 3 months)</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Nov 2026</p>
                  <p className="text-sm text-slate-500">Pilot Cycle 2 begins</p>
                  <Badge className="mt-1 bg-amber-100 text-amber-700">Current</Badge>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Apr 2026</p>
                  <p className="text-sm text-slate-500">CDS Cycle 1 curation submitted</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">End Apr 2026</p>
                  <p className="text-sm text-slate-500">Curation deadline</p>
                  <Badge className="mt-1 bg-blue-100 text-blue-700">In Review</Badge>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <Truck className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Jan 2028</p>
                  <p className="text-sm text-slate-500">First CDS delivery to all schools</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Recent Activity</CardTitle>
          <CardDescription>Last 7 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { time: 'Today 9:14am', text: 'Civica submitted 34 titles for CDS Cycle 1 2028 review', type: 'info' },
              { time: 'Yesterday 4:22pm', text: 'OpenTrolley submitted 12 quotes', type: 'info' },
              { time: 'Yesterday 2:10pm', text: 'RGS completed title selections ($2,847.50 committed)', type: 'success' },
              { time: 'Mon', text: '3 curated titles rejected by MOE HQ — replacement due', type: 'warning' },
              { time: 'Fri', text: 'Pilot Cycle 1 Oct delivery confirmed — all 5 schools acknowledged', type: 'success' },
            ].map((activity, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="text-xs text-slate-500 w-32 shrink-0">{activity.time}</div>
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'success' ? 'bg-emerald-500' :
                  activity.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                }`} />
                <div className="flex-1 text-sm text-slate-700">{activity.text}</div>
                <Button variant="ghost" size="sm" className="text-[#2E5F8A]">
                  View <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
